const WORKER_URL = import.meta.env.VITE_WORKER_URL ?? "http://localhost:8787";

async function request(path, { signal } = {}) {
	const resp = await fetch(`${WORKER_URL}${path}`, { signal });
	if (!resp.ok) {
		let message = `Request failed: ${resp.status}`;
		try {
			const body = await resp.json();
			if (body?.error) message = body.error;
		} catch {
			// non-JSON body, keep default message
		}
		const err = new Error(message);
		err.status = resp.status;
		throw err;
	}
	return resp.json();
}

// `field` narrows the search to one of title/author/publisher/series; omit it
// (or pass null) for a general search across all fields.
// `sort: "new"` lists the newest works first (the comics trade watch).
export function searchBooks(query, { field = null, limit = 20, sort = null, signal } = {}) {
	const params = new URLSearchParams({ q: query, limit: String(limit) });
	if (field) params.set("field", field);
	if (sort) params.set("sort", sort);
	return request(`/books/search?${params}`, { signal });
}

export function getBook(olId, { signal } = {}) {
	return request(`/book/${olId}`, { signal });
}

// Search book SERIES (Wikidata-backed). `by="name"` (default) matches the term
// against series names (resolves aliases like "Game of Thrones" → "A Song of Ice
// and Fire"); `by="author"` matches it against a writer and returns their series
// (e.g. "Salvatore"). Returns [{ wd_id, title, author, book_count }].
export function searchBookSeries(query, { by = "name", signal } = {}) {
	const params = new URLSearchParams({ q: query });
	if (by && by !== "name") params.set("by", by);
	return request(`/books/series?${params}`, { signal });
}

// All books in a series, ordered. Returns
// { wd_id, title, author, books: [{ wd_id, title, ordinal, published_year, ol_id, cover_url }] }.
export function getBookSeries(wdId, { signal } = {}) {
	return request(`/books/series/${wdId}`, { signal });
}

// The series a specific book belongs to + its other entries (for the "More in
// this series" section on the book detail screen). Resolves the book's Open
// Library id → Wikidata series. Returns { wd_id, title, author, books } —
// wd_id null when the book isn't in a series Wikidata knows about.
export function getBookSeriesForBook(olId, { signal } = {}) {
	return request(`/book/${olId}/series`, { signal });
}

// A work's editions (specific publications), sourced from Open Library. Returns
// { size, next_offset, editions }. Paginate via next_offset until it's null.
export function getBookEditions(olId, { limit, offset, signal } = {}) {
	const params = new URLSearchParams();
	if (limit != null) params.set("limit", String(limit));
	if (offset != null) params.set("offset", String(offset));
	const qs = params.toString();
	return request(`/book/${olId}/editions${qs ? `?${qs}` : ""}`, { signal });
}

// The Open Library work for an ISBN (imports) → a search-row shape; 404 if unknown.
export function getBookByIsbn(isbn, { signal } = {}) {
	return request(`/books/isbn?isbn=${encodeURIComponent(isbn)}`, { signal });
}
